import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type BrandDetailQuery,
  DeleteUserDocument,
  type DeleteUserMutation,
  type DeleteUserMutationVariables,
  type UserScopeAccessEnum,
  type UserScopeEnum,
} from "@src/__generated__/graphql";
import type { BrandDetailQueryVariables } from "@src/__generated__/urql-graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { CreateUserModalStore } from "@src/stores/CreateUserModalStore";
import type { TRefetch } from "@src/utils/apolloHelpers";
import { assert } from "@src/utils/types";
import { userScopeToType } from "@src/utils/userScopeToType";
import { orderBy } from "lodash";
import {
  action,
  computed,
  type IObservableArray,
  makeObservable,
  observable,
} from "mobx";
import { BrandModel, ContactModel } from "../../clients/detail/models";
import type { TContact } from "./CardContactPersons";
import { FormBrandDetail } from "./FormBrandDetail";

export class Store {
  refetch?: TRefetch<BrandDetailQueryVariables, BrandDetailQuery>;

  @observable brand: BrandDetailQuery["brand"];
  form = new FormBrandDetail();
  contacts: IObservableArray<ContactModel> = observable.array([]);
  @observable.ref clients: ClientModel[] = [];
  @observable.ref scopedUserIdToDelete: string | null = null;

  deleteScopedUserMutator = new MutationHelper<
    DeleteUserMutation,
    DeleteUserMutationVariables
  >(DeleteUserDocument);

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  fromBrandDetailQuery(src: BrandDetailQuery) {
    assert(src.brand);
    this.brand = src.brand;
    this.contacts = observable.array(
      src.brand.contacts.map((i) => new ContactModel(i)) ?? [],
    );
    this.clients = src.clientsSimpleMap?.map((i) => new ClientModel(i)) ?? [];
    this.form.responsible_person_id.onChange(
      src.brand?.responsiblePerson?.id || "",
    );
    this.form.other_responsible_persons_ids.onChange(
      src.brand?.otherResponsiblePersons?.map((person) => person.id) || [],
    );
  }

  getScopedUserScopeAccessLabel(scopeAccess?: UserScopeAccessEnum | null) {
    return CreateUserModalStore.GetScopeAccessOptions().find(
      (option) => option.value === scopeAccess,
    )?.label;
  }

  @action.bound handleAddClientAccess(brandId: string) {
    this.appStore.createUserModalStore.modalState.onOpen({
      userType: "client",
      brandIds: [brandId],
      onSubmit: () => {
        this.refetch?.({ id: brandId });
      },
    });
  }

  @action.bound handleScopedUserEdit(
    brandId: string,
    userId: string,
    scope: UserScopeEnum,
  ) {
    this.appStore.editUserModalStore.modalState.onOpen({
      userId,
      userType: userScopeToType(scope),
      onSubmit: () => {
        this.refetch?.({
          id: brandId,
        });
      },
    });
  }

  @computed get isScopedUserDeleteModalOpen(): boolean {
    return this.scopedUserIdToDelete !== null;
  }

  @computed get scopedUserToDelete() {
    return this.brand?.users.find(
      (scopedUser) => scopedUser.id === this.scopedUserIdToDelete,
    );
  }

  @action.bound handleScopedUserDelete(userId: string) {
    this.scopedUserIdToDelete = userId;
  }

  @action.bound handleScopedUserDeleteCancel() {
    this.scopedUserIdToDelete = null;
  }

  @action.bound async handleScopedUserDeleteConfirm() {
    if (!this.brand) {
      this.appStore.UIStore.toast({
        status: "error",
      });
      captureEvent({
        message: "FE: Missing brand data.",
      });
      return;
    }
    if (!this.scopedUserIdToDelete) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Missing ID of user to delete, please contact support and try again later.`,
      });
      captureEvent({
        message: "FE: Missing scopedUserIdToDelete.",
      });
      return;
    }

    const [data, error] = await this.deleteScopedUserMutator.mutate({
      id: this.scopedUserIdToDelete,
    });

    if (data && !error) {
      this.brand.users = this.brand.users.filter(
        (scopedUser) => scopedUser.id !== data.deleteUser.id,
      );
    }

    this.handleScopedUserDeleteCancel();
  }

  @action.bound removeContact(id: string) {
    const contact = this.contacts.find((i) => i.id === id);
    contact && this.contacts.remove(contact);
  }

  @action.bound addContact(contact: TContact) {
    this.contacts.push(new ContactModel(contact));
  }

  @action.bound
  updateContact(contact: TContact) {
    const contactModel = this.contacts.find((i) => i.id === contact.id);
    if (contactModel) {
      contactModel.first_name = contact.first_name ?? "";
      contactModel.last_name = contact.last_name ?? "";
      contactModel.email = contact.email ?? "";
      contactModel.phone = contact.phone ?? "";
    }
  }

  @computed get brandForModal(): BrandModel {
    assert(this.brand);
    return new BrandModel({
      ...this.brand,
      archived_at: this.brand.archived_at
        ? new Date(this.brand.archived_at)
        : undefined,
    });
  }

  @computed get clientOptions() {
    return orderBy(
      this.clients.map((i) => ({ label: i.name, value: i.id })),
      ["label"],
      ["asc"],
    );
  }
}

class ClientModel {
  @observable id: string;

  @observable name: string;
  @observable billingEntities?: {
    id: string;
    internal_name: string;
  }[] = [];

  constructor(src: Omit<ClientModel, "__nominal" | "billingEntitiesOptions">) {
    makeObservable(this);
    this.id = src.id;
    this.name = src.name;
    this.billingEntities = src.billingEntities;
  }

  @computed get billingEntitiesOptions() {
    return this.billingEntities?.map((i) => ({
      label: i.internal_name,
      value: i.id,
    }));
  }
}
